import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { SearchOutlined } from "@ant-design/icons";
import { Input, Skeleton, Space } from "antd";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { ApiNames, getPharmacyPatientByIdAPI } from "../../api/api";
import { WHITE } from "../../constant/colors";
import { ORDER_VIEW } from "../../constant/filters";
import { searchTypes, ViewType } from "../../constant/general";
import { Routes } from "../../constant/routes";
import { history } from "../../router/MainRouter";
import { default as ACTION, default as ACTIONS } from "../../store/action";
import {
  selectCurrentPage,
  selectFilterList,
  selectFilterListStatuses,
  selectFilters,
  selectIsFetching,
  selectIsFilterListFiltersModified,
  selectIsLoadingEnabled,
  selectOrdersTableSearchedText,
  selectPatients,
  selectSorting,
  selectTableColumnsModified,
  selectTableSorterModified,
  selectTotalPatients,
  selectViewType
} from "../../store/selector";
import { isModifierClick } from "../../utils/misc";
import { ColumnSelector } from "../ColumnSelector";
import { OrdersCalendar } from "../OrdersCalendar";
import { OrderFilters } from "../OrdersFilter";
import { OrdersTable } from "../OrdersTable";
import { FilterListControls } from "./FilterListControls";

const DashboardScreen = styled.div`
  position: relative;
  background: ${WHITE};
  border-radius: 10px;
  padding-top: 6px;
  height: calc(100vh - 136px);
`;

const ContainerStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  padding-left: 50px;
  padding-top: 24px;
  transition: all 0.3s ease;
`;

const DEFAULT_PAGE = 1;
const DEFAULT_SORTER = {};

const FilterListView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const NAME_ID = searchTypes.name_id.key;
  const setCurrentPageAct = (page) => dispatch(ACTIONS.setCurrentPage(page));
  const isInitialMount = useRef(true);
  const searchText = useSelector(selectOrdersTableSearchedText) || "";
  const currentPage = useSelector(selectCurrentPage);
  const filters = useSelector(selectFilters);
  const sorting = useSelector(selectSorting);
  const isFetching = useSelector(selectIsFetching([ApiNames.FindSavedListById]));
  const isFetchingOrdersTable = useSelector(selectIsFetching([ApiNames.Search]));
  const filterList = useSelector(selectFilterList);
  const filterListStatuses = useSelector(selectFilterListStatuses);
  const filterListFiltersModified = useSelector(selectIsFilterListFiltersModified);
  const tableColumnsModified = useSelector(selectTableColumnsModified);
  const tableSortingModified = useSelector(selectTableSorterModified);
  const patients = useSelector(selectPatients);
  const total = useSelector(selectTotalPatients);
  const isLoadingEnabled = useSelector(selectIsLoadingEnabled);
  const viewType = useSelector(selectViewType);

  const queryParameters = new URLSearchParams(window.location.search);
  const patientId = queryParameters.get("patient");
  const pharmacyPatientId = parseInt(queryParameters.get("pharmacyPatient"));

  const { savedListViewType } = useParams();

  const [filtersVisible, setFiltersVisible] = useState(false);
  const [searchInput, setSearchInput] = useState(searchText);
  const { filterListId } = useParams();
  const setOrdersTableSearchTextAct = (ordersTableSearchText) =>
    dispatch(ACTIONS.setOrdersTableSearchText(ordersTableSearchText));
  const searchPatientTextFiltersAct = (page, text, searchBy, filters) =>
    dispatch(ACTIONS.searchPatientTextFiltersAction(page, text, searchBy, filters));
  const setFiltersAct = (filters, apiName, page, sorting) =>
    dispatch(ACTIONS.setFiltersAction(filters, apiName, page, sorting));
  const setPatientModal = (show, tab) => dispatch(ACTIONS.setShowPatientModal(show, tab));
  const setEnableCycleFlashAct = (enableFlash) => dispatch(ACTIONS.setEnableCycleFlash(enableFlash));
  const setPatientAct = (pharmacyPatient, patientId, orderId) =>
    dispatch(ACTIONS.setPatientAction(pharmacyPatient, patientId, orderId));
  const getPatientOrderAct = (pharmacyPatient) => dispatch(ACTIONS.getPatientOrderAction(pharmacyPatient));
  const { orderViewFilter: orderViewType } = filters;
  const setSavedListViewTypeAct = (savedListViewType) => dispatch(ACTIONS.setSavedListViewType(savedListViewType));
  const setCalendarOrderDateFilterACT = (calendarOrderDateFilter) =>
    dispatch(ACTIONS.setCalendarOrderDateFilter(calendarOrderDateFilter));
  const searchSavedListByIdAct = (filterListId) => dispatch(ACTION.searchSavedListByIdAction(filterListId));
  const markSavedFilterAsChanged = (filterListID, isModified) =>
    dispatch(ACTIONS.markSavedFilterAsChanged(filterListID, isModified));
  const updateSavedFilterViewParameters = (filterListID) => (page, sorter) =>
    dispatch(ACTION.updateSavedFilterViewParameters(filterListID, page, sorter));
  const updateSavedFilterViewParametersAct = updateSavedFilterViewParameters(filterListId);

  useEffect(() => {
    const fetchPatient = async (patientId, pharmacyPatientId) => {
      if (patientId && pharmacyPatientId) {
        setPatientModal(true);
        const { data: externalPatient } = await getPharmacyPatientByIdAPI(pharmacyPatientId);
        setPatientAct(pharmacyPatientId, patientId, externalPatient.currentOrder.id);
        dispatch(ACTION.setCurrentCycle(externalPatient));
      }
    };
    fetchPatient(patientId, pharmacyPatientId);
  }, [patientId, pharmacyPatientId]);

  const setPatients = (patients) => dispatch(ACTIONS.setPatients(patients));
  const fetchOrders = (page, apiName = ApiNames.Search, sorter) => {
    setFiltersAct(
      {
        ...filters,
        forceCallApi: true
      },
      apiName,
      page,
      sorter
    );
  };

  const debouncedSearch = useCallback(
    debounce((text) => searchPatientTextFiltersAct(1, text, NAME_ID, filters), 500),
    [filters]
  );

  useEffect(() => {
    setFiltersVisible(false);
  }, [filterListId]);

  useEffect(() => {
    if (filterListId && filterListStatuses.id !== filterListId) {
      //Note: Reset the filter parameters when changing the view to another filter page.
      updateSavedFilterViewParametersAct(DEFAULT_PAGE, DEFAULT_SORTER);
      setPatients([]);
    }

    // Note: we need to fetch data always to make data consistent for the user.
    searchSavedListByIdAct(filterListId);

    if (filterListId) {
      // Note: Reset the filter modified status to mark it as not modified.
      markSavedFilterAsChanged(filterListId, false);
    }
  }, [filterListId]);

  useEffect(() => {
    if (filterList?.id && (tableColumnsModified || filterListFiltersModified)) {
      markSavedFilterAsChanged(filterList?.id, true);
    }
  }, [tableColumnsModified, filterListFiltersModified, tableSortingModified]);

  useEffect(() => {
    if (!filterListId) {
      history.push(`${Routes.ROOT}${viewType}`);
    }
  }, [filterListId, history, viewType]);

  const onHeaderCell = (column) => ({
    id: `${column.id}-sorting`
  });

  const onClickTable = (modalTabIndex = 0) => ({
    onHeaderCell,
    onCell: (patient) => ({
      onClick: (e) => {
        if (isModifierClick(e)) {
          return;
        }

        if (
          orderViewType === ORDER_VIEW.ALL_ORDERS.value &&
          modalTabIndex === 0 &&
          patient.currentOrder.id !== patient.pharmacyPatientCurrentOrder
        ) {
          setPatientModal(true, 1);
          setEnableCycleFlashAct(true);
        } else {
          setPatientModal(true, modalTabIndex);
        }
        const { id } = patient;
        setPatientAct(id, patient.patient.id, patient.currentOrder.id);
        getPatientOrderAct(patient.id);
      }
    })
  });

  const onClickCalendar = (patient) => {
    if (
      orderViewType === ORDER_VIEW.ALL_ORDERS.value &&
      patient.currentOrder.id !== patient.pharmacyPatientCurrentOrder
    ) {
      setPatientModal(true, 1);
      setEnableCycleFlashAct(true);
    } else {
      setPatientModal(true, 0);
    }
    const { id } = patient;
    setPatientAct(id, patient.patient.id, patient.currentOrder.id);
    getPatientOrderAct(patient.id);
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setOrdersTableSearchTextAct(searchInput);
    if (searchInput && searchInput.trim().length > 0) {
      setCurrentPageAct(1);
      debouncedSearch(searchInput);
    } else if (!searchInput) {
      debouncedSearch.cancel();
      setOrdersTableSearchTextAct("");
      fetchOrders(currentPage, ApiNames.SearchWithoutRefresh, sorting);
    }
  }, [searchInput]);

  useEffect(() => {
    setSavedListViewTypeAct(savedListViewType);
  }, [savedListViewType]);

  const onViewTypeChange = (e) => {
    const viewType = e.target.value;
    setSavedListViewTypeAct(viewType);
    history.push(`${Routes.ROOT}${Routes.SAVEDLISTS}/${filterListId}/${viewType}`);
    setCurrentPageAct(1);
    setOrdersTableSearchTextAct("");
    setSearchInput("");
    if (viewType === ViewType.List.key) fetchOrders(currentPage, ApiNames.Search, sorting);
  };

  return (
    <DashboardScreen id="filter-list-dashboard" direction={"vertical"}>
      {!isFetching ? (
        <>
          <FilterListControls onViewTypeChange={onViewTypeChange} />
          <ContainerStyled>
            {savedListViewType === ViewType.List.key && (
              <>
                <OrderFilters
                  isSavedListView={true}
                  isListView={viewType === ViewType.List.key || savedListViewType === ViewType.List.key}
                  setFiltersVisible={setFiltersVisible}
                  filtersVisible={filtersVisible}
                />
                <div style={{ flex: "1 1 auto", overflow: "hidden" }}>
                  <Space
                    style={{
                      width: "100%",
                      paddingRight: 50,
                      paddingLeft: filtersVisible ? 8 : 115,
                      marginTop: "10px",
                      justifyContent: "space-between",
                      display: "flex"
                    }}
                  >
                    <div id="list_search">
                      <Input
                        allowClear
                        value={searchInput}
                        placeholder={t("search_placeholder")}
                        prefix={<SearchOutlined />}
                        style={{
                          width: 300
                        }}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </div>
                    {patients.length > 0 && <ColumnSelector />}
                  </Space>
                  <div>
                    <OrdersTable
                      setSearchInput={setSearchInput}
                      searchInput={searchInput}
                      patients={patients}
                      onClickTable={onClickTable}
                      onHeaderCell={onHeaderCell}
                      total={total}
                      currentPage={currentPage}
                      isFetching={isFetchingOrdersTable}
                      fetchOrders={fetchOrders}
                      isLoadingEnabled={isLoadingEnabled}
                      padding={48}
                      isSavedListView={true}
                      filtersVisible={filtersVisible}
                    />
                  </div>
                </div>
              </>
            )}
          </ContainerStyled>
          {savedListViewType === ViewType.Calendar.key && (
            <OrdersCalendar
              patients={patients}
              onClickCalendar={onClickCalendar}
              onCalenderViewChange={(fromDate, toDate) => (
                setCalendarOrderDateFilterACT({ fromDate, toDate }),
                setFiltersAct({ ...filters, forceCallApi: true }, ApiNames.Search, 1, {})
              )}
              isFetching={isFetchingOrdersTable}
            />
          )}
        </>
      ) : (
        <>
          <Skeleton.Button style={{ width: 100, height: 40 }} active />
          <Skeleton style={{ paddingTop: 16 }} active paragraph={{ rows: 6 }} />
          <Skeleton style={{ paddingTop: 32 }} active paragraph={{ rows: 6 }} />
        </>
      )}
    </DashboardScreen>
  );
};

export { FilterListView };
